import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import {
	TitleInsurancePillData,
	getCounselFeeHover,
	getPremiumPillHover,
} from '@core/util/title-insurance';
import {
	MatterManualTitleInsurance,
	TitleInsuranceDocument,
	TitleInsurancePolicy,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import {
	CostBreakdownComponent,
	FCTTitleInsuranceCostBreakdownData,
} from '../../../fct/components/cost-breakdown/cost-breakdown.component';
import { TitlePlusService } from '@services/title-plus/title-plus.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { MatterBusinessUser } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { TestHooks } from './title-plus-issued.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

@UntilDestroy()
@Component({
	selector: 'app-title-plus-issued',
	templateUrl: './title-plus-issued.component.html',
	styleUrls: ['./title-plus-issued.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TitlePlusIssuedComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public titleInsurance: MatterManualTitleInsurance;
	public displayPill = 'none';
	public premiumPillData: TitleInsurancePillData;
	public counselPillData: TitleInsurancePillData;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;
	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@Select(SelectedMatter.titleInsuranceAmountDueByClient)
	amountDueByClient$: Observable<number>;
	@Select(SelectedMatter.getTitleInsuranceDirectionOfAmount)
	titleInsuranceDOA$: Observable<DirectionOfFeeType>;
	@Select(SelectedMatter.titleInsurancePolicies) policies$: Observable<
		TitleInsurancePolicy[]
	>;
	@Select(AuthState.getUserId) userId: string;

	drawerService = inject(DrawerService);

	constructor(
		private dialog: MatDialog,
		private analytics: AnalyticsService,
		private titlePlus: TitlePlusService,
		private changeDetectorRef: ChangeDetectorRef,
	) {}

	ngOnInit() {
		combineLatest([this.titleInsurance$, this.titleInsuranceDOA$])
			.pipe(untilDestroyed(this))
			.subscribe(([_titleInsurance, titleInsuranceDOA]) => {
				if (_titleInsurance) {
					this.titleInsurance = cloneDeep(_titleInsurance);
				}

				this.premiumPillData = getPremiumPillHover(titleInsuranceDOA);
				this.counselPillData = getCounselFeeHover(
					_titleInsurance,
					titleInsuranceDOA,
				);
			});
	}

	policyTypeString(type: string): string {
		if (type === 'lender') {
			return 'Lender policy';
		}

		if (type === 'owner') {
			return 'Homeowner policy';
		}

		return type;
	}

	async openDoc(doc: TitleInsuranceDocument): Promise<void> {
		try {
			doc['loading'] = true;
			this.openPDF({
				blob: await this.titlePlus.retrieveDocument(doc.url),
				fileName: doc.name,
				fileType: 'application/pdf',
			});
		} finally {
			doc['loading'] = false;
			this.changeDetectorRef.detectChanges();
		}
	}

	showBreakdown() {
		this.analytics.logEvent(AnalyticsEvent.TITLE_INSURANCE_VIEW_FEE_BREAKDOWN, {
			provider: TitleInsuranceProvider.TITLE_PLUS,
		});
		const data: FCTTitleInsuranceCostBreakdownData = {
			type: 'ORDER',
		};
		this.dialog.open(CostBreakdownComponent, { data });
	}

	togglePill(_displayPill: string) {
		this.displayPill = _displayPill;
	}

	openPDF(data: PdfModalData) {
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}
}
